import React, { useCallback, useMemo, useState } from 'react';
import {
  BadgedLink,
  Button,
  Flex,
  Heading1,
  Heading5,
  IconArrowLeftSmallOutline,
  Island,
  RadioButton,
  TextSmall,
} from '@teamleader/ahoy';
import { translate } from '@teamleader/i18n';
import cx from 'classnames';
import { useMultiFactorAuthenticationContext } from './MultiFactorAuthenticationContext';
import styles from './styles.css';
import {
  isEmailMultiFactorAuthentication,
  isSmsMultiFactorAuthentication,
  MultiFactorAuthentication,
  MultiFactorAuthenticationType,
} from '../../../Infrastructure/API/MultiFactorAuthentication/list';
import { EmailIcon, SmsIcon, TotpIcon } from './icons';

interface AlternativeMethodProps {
  authentication: MultiFactorAuthentication;
  isSelected: boolean;
  onSelect: (type: MultiFactorAuthenticationType) => void;
}

const AlternativeMethod = ({ authentication, isSelected, onSelect }: AlternativeMethodProps) => {
  const handleSelect = useCallback(() => {
    onSelect(authentication.provider);
  }, [authentication.provider, onSelect]);

  const title = useMemo(() => {
    switch (authentication.provider) {
      case MultiFactorAuthenticationType.Email:
        return translate('mfa.emailListItemTitle');
      case MultiFactorAuthenticationType.SMS:
        return translate('mfa.smsListItemTitle');
      case MultiFactorAuthenticationType.TOTP:
        return translate('mfa.totpListItemTitle');
    }
  }, [authentication.provider]);

  const ProviderIcon = useMemo(() => {
    switch (authentication.provider) {
      case MultiFactorAuthenticationType.Email:
        return EmailIcon;
      case MultiFactorAuthenticationType.SMS:
        return SmsIcon;
      case MultiFactorAuthenticationType.TOTP:
        return TotpIcon;
    }
  }, [authentication.provider]);

  return (
    <Island
      key={authentication.id}
      padding={3}
      className={cx(styles.alternative, { [styles['alternative--selected']]: isSelected })}
      element="label"
    >
      <Flex alignItems="center" justifyContent="space-between" gap={3}>
        <RadioButton value={authentication.id} checked={isSelected} onChange={handleSelect} />
        <Flex direction="column" gap={1} flex={1}>
          <Heading5>{title}</Heading5>
          {authentication.provider === MultiFactorAuthenticationType.TOTP && (
            <TextSmall>{translate('mfa.totpListItemDescription')}</TextSmall>
          )}
          {isSmsMultiFactorAuthentication(authentication) && <TextSmall>{authentication.number}</TextSmall>}
          {isEmailMultiFactorAuthentication(authentication) && <TextSmall>{authentication.email}</TextSmall>}
        </Flex>
        <ProviderIcon />
      </Flex>
    </Island>
  );
};

interface AlternativeMethodsProps {
  onGoBackToLoginClick: () => void;
  onSubmit: (selectedAuthenticationType: MultiFactorAuthenticationType) => void;
}

const AlternativeMethods = ({ onGoBackToLoginClick, onSubmit }: AlternativeMethodsProps) => {
  const { authentications, defaultAuthentication } = useMultiFactorAuthenticationContext();

  const [selectedAuthenticationType, setSelectedAuthenticationType] = useState<MultiFactorAuthenticationType>();

  const selectedAuthenticationTypeWithDefaultFallback = selectedAuthenticationType ?? defaultAuthentication?.provider;

  const buttonText = useMemo(() => {
    switch (selectedAuthenticationTypeWithDefaultFallback) {
      case MultiFactorAuthenticationType.Email:
      case MultiFactorAuthenticationType.SMS:
        return translate('mfa.sendCode');
      case MultiFactorAuthenticationType.TOTP:
        return translate('mfa.verify');
    }
  }, [selectedAuthenticationTypeWithDefaultFallback]);

  return (
    <Flex direction="column" gap={5}>
      <Flex direction="column" alignItems="flex-start" gap={2}>
        <BadgedLink onClick={onGoBackToLoginClick} inherit={false} icon={<IconArrowLeftSmallOutline />}>
          <TextSmall>{translate('mfa.goBackToLogin')}</TextSmall>
        </BadgedLink>
        <Heading1>{translate('mfa.getYourSecurityCode')}</Heading1>
        <TextSmall color="neutral" tint="darkest">
          {translate('mfa.chooseMethod')}
        </TextSmall>
      </Flex>
      <Flex direction="column" gap={2}>
        {authentications.map((authentication) => (
          <AlternativeMethod
            key={authentication.id}
            authentication={authentication}
            isSelected={authentication.provider === selectedAuthenticationTypeWithDefaultFallback}
            onSelect={(type) => setSelectedAuthenticationType(type)}
          />
        ))}
      </Flex>
      <Button
        size="large"
        level="primary"
        fullWidth
        type="button"
        onClick={() => {
          if (selectedAuthenticationTypeWithDefaultFallback) {
            onSubmit(selectedAuthenticationTypeWithDefaultFallback);
          }
        }}
      >
        {buttonText}
      </Button>
    </Flex>
  );
};

export default AlternativeMethods;
